import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraFormField, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowDown, ArrowUp, IndentDecrease, IndentIncrease, Plus, Trash2, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { parseDateInput, timestampToDateInput } from '../../lib/localCalendarDate';
import { useRfiQuestions } from './rfiQuestionHooks';
import { REVIEW_STATES, WORK_STATES } from './rfiSectionHooks';
const WORK_LABELS = {
    not_started: 'Not started',
    in_progress: 'In progress',
    complete: 'Complete'
};
const REVIEW_LABELS = {
    not_requested: 'Not requested',
    pending: 'Pending',
    approved: 'Approved',
    changes_requested: 'Changes requested'
};
/** Section readiness: work and review combined. Never feeds the RFI's own status. */ export function sectionReadiness(s) {
    if (s.reviewState === 'changes_requested') return {
        label: 'Changes requested',
        ready: false,
        variant: 'warning'
    };
    if (s.workState === 'complete' && s.reviewState === 'approved') return {
        label: 'Ready',
        ready: true,
        variant: 'success'
    };
    if (s.workState === 'complete' && s.reviewState === 'pending') return {
        label: 'Awaiting review',
        ready: false,
        variant: 'info'
    };
    if (s.workState === 'complete') return {
        label: 'Complete, unreviewed',
        ready: false,
        variant: 'info'
    };
    if (s.workState === 'in_progress') return {
        label: 'In progress',
        ready: false,
        variant: 'info'
    };
    return {
        label: 'Not started',
        ready: false,
        variant: 'neutral'
    };
}
export function RfiSectionRow({ section, depth, people, meId, canMoveUp, canMoveDown, canIndent, canOutdent, onMoveUp, onMoveDown, onIndent, onOutdent, actions }) {
    const [draft, setDraft] = useState(null);
    const [pending, setPending] = useState(null);
    const questions = useRfiQuestions(section.rfiResponseProjectId);
    const readiness = sectionReadiness(section);
    const id = section.id;
    const commitTitle = ()=>{
        const next = (draft ?? section.title).trim();
        setDraft(null);
        if (next && next !== section.title) actions.update({
            sectionId: id,
            title: next
        });
    };
    const tryRemove = async (confirm)=>{
        const resp = await actions.remove({
            sectionId: id,
            confirm
        });
        if (!resp) return;
        if (resp.removed) {
            setPending(null);
            return;
        }
        setPending({
            warning: resp.warning,
            questions: resp.unaddressedQuestions.map((q)=>q.questionRef)
        });
    };
    const personOptions = (selected)=>{
        const known = people.some((p)=>p.userId === selected);
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("option", {
                    value: "",
                    children: "Unassigned"
                }),
                selected && !known ? /*#__PURE__*/ _jsx("option", {
                    value: selected,
                    children: selected
                }) : null,
                people.map((p)=>/*#__PURE__*/ _jsx("option", {
                        value: p.userId,
                        children: p.userId === meId ? `${p.name || p.email} (me)` : p.name || p.email
                    }, p.userId))
            ]
        });
    };
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-2 px-3 py-2",
        style: {
            paddingLeft: `${0.75 + depth * 1.5}rem`
        },
        "data-testid": Testids.rfiOutline.sectionRow,
        id: `rfi-section-${id}`,
        tabIndex: -1,
        "data-section-id": id,
        "data-depth": depth,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "text-sm space-y-1",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Mapped questions and instructions (answer state is separate)"
                    }),
                    questions.isLoading ? /*#__PURE__*/ _jsx("output", {
                        children: "Loading mapped questions…"
                    }) : questions.isError ? /*#__PURE__*/ _jsxs("p", {
                        role: "alert",
                        children: [
                            "Couldn't load mapped questions.",
                            ' ',
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                type: "button",
                                onClick: ()=>questions.refetch(),
                                children: "Retry questions"
                            })
                        ]
                    }) : questions.data?.questions.some((q)=>q.sectionIds.includes(id)) ? questions.data.questions.filter((q)=>q.sectionIds.includes(id)).map((q)=>/*#__PURE__*/ _jsxs("a", {
                            className: "block",
                            href: `#rfi-question-${q.id}`,
                            "data-testid": Testids.rfiOutline.mappedQuestion,
                            children: [
                                q.source?.originalText,
                                " · ",
                                q.answerState
                            ]
                        }, q.id)) : /*#__PURE__*/ _jsx("p", {
                        children: "No mapped questions. Map an item from Questions and instructions."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        className: "min-w-48 flex-1",
                        "aria-label": "Section title",
                        value: draft ?? section.title,
                        onChange: (e)=>setDraft(e.target.value),
                        onBlur: commitTitle,
                        onKeyDown: (e)=>{
                            if (e.key === 'Enter') e.currentTarget.blur();
                            if (e.key === 'Escape') setDraft(null);
                        },
                        "data-testid": Testids.rfiOutline.titleInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: readiness.variant,
                        "data-testid": Testids.rfiOutline.readinessBadge,
                        children: readiness.label
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Move up",
                        disabled: !canMoveUp,
                        onClick: onMoveUp,
                        "data-testid": Testids.rfiOutline.moveUpButton,
                        iconLeft: /*#__PURE__*/ _jsx(ArrowUp, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Move down",
                        disabled: !canMoveDown,
                        onClick: onMoveDown,
                        "data-testid": Testids.rfiOutline.moveDownButton,
                        iconLeft: /*#__PURE__*/ _jsx(ArrowDown, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Indent",
                        disabled: !canIndent,
                        onClick: onIndent,
                        "data-testid": Testids.rfiOutline.indentButton,
                        iconLeft: /*#__PURE__*/ _jsx(IndentIncrease, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Outdent",
                        disabled: !canOutdent,
                        onClick: onOutdent,
                        "data-testid": Testids.rfiOutline.outdentButton,
                        iconLeft: /*#__PURE__*/ _jsx(IndentDecrease, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Add subsection",
                        onClick: ()=>actions.addChild(id),
                        "data-testid": Testids.rfiOutline.addChildButton,
                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                            className: "h-4 w-4"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        "aria-label": "Remove section",
                        onClick: ()=>void tryRemove(false),
                        "data-testid": Testids.rfiOutline.removeButton,
                        iconLeft: /*#__PURE__*/ _jsx(Trash2, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            pending ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                className: "space-y-2 rounded-md border border-warning p-2 text-sm",
                "data-testid": Testids.rfiOutline.removeWarning,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: pending.warning || 'Questions are still mapped to this section.'
                    }),
                    pending.questions.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                        className: "list-disc pl-5",
                        children: pending.questions.map((q)=>/*#__PURE__*/ _jsx("li", {
                                "data-testid": Testids.rfiOutline.unaddressedQuestion,
                                children: q
                            }, q))
                    }) : null,
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "destructive",
                                onClick: ()=>void tryRemove(true),
                                "data-testid": Testids.rfiOutline.removeConfirmButton,
                                children: "Remove anyway"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>setPending(null),
                                "data-testid": Testids.rfiOutline.removeCancelButton,
                                children: "Keep section"
                            })
                        ]
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-3 text-xs",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Owner",
                        htmlFor: `${id}-owner`,
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            id: `${id}-owner`,
                            className: "w-44",
                            value: section.ownerUserId,
                            onChange: (e)=>actions.update({
                                    sectionId: id,
                                    ownerUserId: e.target.value
                                }),
                            "data-testid": Testids.rfiOutline.ownerSelect,
                            children: personOptions(section.ownerUserId)
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        disabled: !meId || section.ownerUserId === meId,
                        onClick: ()=>actions.update({
                                sectionId: id,
                                ownerUserId: meId
                            }),
                        "data-testid": Testids.rfiOutline.assignMeButton,
                        iconLeft: /*#__PURE__*/ _jsx(UserCheck, {
                            className: "h-4 w-4"
                        }),
                        children: "Assign me"
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Reviewer",
                        htmlFor: `${id}-reviewer`,
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            id: `${id}-reviewer`,
                            className: "w-44",
                            value: section.reviewerUserId,
                            onChange: (e)=>e.target.value ? actions.update({
                                    sectionId: id,
                                    reviewerUserId: e.target.value
                                }) : actions.update({
                                    sectionId: id,
                                    clearReviewer: true
                                }),
                            "data-testid": Testids.rfiOutline.reviewerSelect,
                            children: personOptions(section.reviewerUserId)
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Internal due",
                        htmlFor: `${id}-due`,
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            id: `${id}-due`,
                            type: "date",
                            value: timestampToDateInput(section.internalDue),
                            onChange: (e)=>{
                                const d = parseDateInput(e.target.value);
                                if (d) actions.update({
                                    sectionId: id,
                                    internalDue: d
                                });
                                else actions.update({
                                    sectionId: id,
                                    clearInternalDue: true
                                });
                            },
                            "data-testid": Testids.rfiOutline.internalDueInput
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Work",
                        htmlFor: `${id}-work`,
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            id: `${id}-work`,
                            className: "w-36",
                            value: section.workState,
                            onChange: (e)=>actions.update({
                                    sectionId: id,
                                    workState: e.target.value
                                }),
                            "data-testid": Testids.rfiOutline.workStateSelect,
                            children: WORK_STATES.map((s)=>/*#__PURE__*/ _jsx("option", {
                                    value: s,
                                    children: WORK_LABELS[s]
                                }, s))
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Review",
                        htmlFor: `${id}-review`,
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            id: `${id}-review`,
                            className: "w-44",
                            value: section.reviewState,
                            onChange: (e)=>actions.update({
                                    sectionId: id,
                                    reviewState: e.target.value
                                }),
                            "data-testid": Testids.rfiOutline.reviewStateSelect,
                            children: REVIEW_STATES.map((s)=>/*#__PURE__*/ _jsx("option", {
                                    value: s,
                                    children: REVIEW_LABELS[s]
                                }, s))
                        })
                    })
                ]
            })
        ]
    });
}
